import * as R from 'ramda';
import { ModuleListReloader } from 'utils/ModuleListReloader/ModuleListReloader';
import { reduxHelper } from 'utils/reduxHelper';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';
import { stateHelper } from 'utils/stateHelper';
import { DeepPartial } from 'utils/Types';

import { GlobalState } from 'reactApp/core/rootReducer';

type BasicParams = {
    dataFieldKey?: string;
    entityName: string;
    entityNamePublic: string;
    apiMethods: any;
    shouldLoadItemAfterRemove?: boolean;
    key?: any;
    shouldProvideKeyToApi?: boolean;
};
type CommonListModuleParams<D> = BasicParams & {
    idKey?: string;
    inititalList?: D[];
    onItemEdited?: (...args: any[]) => any;
    consumerUpdateInterval?: number;
};

type CommonModuleParams<Item> = BasicParams & {
    initialData?: Item;
};

const createCommonListModule = <
    Item,
    CreateItemData = DeepPartial<Item>,
    UpdateItemData = Partial<Item>
>({
    entityName,
    entityNamePublic,
    apiMethods,
    shouldLoadItemAfterRemove,
    key,
    shouldProvideKeyToApi,
    idKey = 'id',
    onItemEdited,
    inititalList = [],
    consumerUpdateInterval,
}: CommonListModuleParams<Item>) => {
    const dataFieldKey = 'list';

    type InitialState = {
        list: Item[];
        isLoaded: boolean;
        isLoading: boolean;
        hasError: boolean;
    };

    const initialState: InitialState = {
        [dataFieldKey]: inititalList,
        isLoaded: false,
        isLoading: false,
        hasError: false,
    } as any;

    const module =
        key == null
            ? stateHelper.createModuleState<InitialState>(
                  entityName,
                  initialState,
              )
            : stateHelper.getMultiModule<InitialState>({
                  name: entityName,
                  key,
                  initialState,
              });

    if (shouldProvideKeyToApi) {
        apiMethods = _applyKeyToApiMethods(apiMethods, key);
    }

    const initialLoadList = reduxHelper.createLoadListAction({
        module,
        apiMethod: apiMethods.loadList,
        entityName,
        fieldKey: dataFieldKey,
    });

    const updateList = reduxHelper.createUpdateListAction({
        module,
        apiMethod: apiMethods.loadList,
        entityName,
        entityNameLocal: entityNamePublic,
        fieldKey: dataFieldKey,
    });

    const loadListSavingState = reduxHelper.createLoadListSavingStateAction({
        loadList: initialLoadList,
        updateList,
        module,
    });

    const loadItem = reduxHelper.createLoadItemAction({
        module,
        entityName,
        entityNamePublic,
        apiMethod: apiMethods.loadItem,
        listFieldKey: dataFieldKey,
        idKey,
    });

    const updateItem = reduxHelper.createLoadItemAction({
        module,
        entityName,
        entityNamePublic,
        apiMethod: apiMethods.updateItem,
        listFieldKey: dataFieldKey,
        idKey,
    });

    const createItem = reduxHelper.makeCreateAction({
        module,
        apiMethod: apiMethods.createItem,
        entityName,
        listKey: dataFieldKey,
        idKey,
    });

    const createFewItems = reduxHelper.multiplyAction(createItem);

    const removeItem = reduxHelper.makeRemoveAction({
        module,
        apiMethod: apiMethods.removeItem,
        entityName,
        entityNamePublic,
        listKey: dataFieldKey,
        idKey,
        loadItemOnRemoveAction: shouldLoadItemAfterRemove
            ? loadItem
            : undefined,
    });

    const removeFewItems = reduxHelper.multiplyAction(removeItem);

    const editItem = reduxHelper.makeEditAction<Item, UpdateItemData>({
        module,
        idKey,
        listKey: dataFieldKey,
        apiMethod: apiMethods.editItem,
        onItemEdited,
    });

    const {
        getList,
        getIsLoaded,
        getIsLoading,
        getHasError,
        getItem,
    } = reduxHelper.makeStandardSelectors<Item>(module, {
        listKey: dataFieldKey,
        idKey,
    });

    const { addConsumer, removeConsumer } = new ModuleListReloader({
        loadList: updateList,
        initialLoadList,
        selectIsLoaded: getIsLoaded,
        updateTimeout: consumerUpdateInterval,
    });

    const Selectors = {
        getList,
        getIsLoaded,
        getIsLoading,
        getHasError,
        getItem,
    };

    const TypedActions: {
        addConsumer: () => ReduxTypeUtils.ThunkResult<Promise<void>>;
        removeConsumer: () => ReduxTypeUtils.ThunkResult<Promise<void>>;
        initialLoadList: () => ReduxTypeUtils.ThunkResult<Promise<Item[]>>;
        loadList: () => ReduxTypeUtils.ThunkResult<Promise<Item[]>>;
        loadListSavingState: () => ReduxTypeUtils.ThunkResult<Promise<Item[]>>;
        loadItem: (id: any) => ReduxTypeUtils.ThunkResult<Promise<Item>>;
        updateItem: (id: any) => ReduxTypeUtils.ThunkResult<Promise<Item>>;
        createItem: (
            data: CreateItemData,
        ) => ReduxTypeUtils.ThunkResult<Promise<Item>>;
        createFewItems: (
            data: Partial<Item>,
        ) => ReduxTypeUtils.ThunkResult<Promise<void>>;
        removeItem: (id: any) => ReduxTypeUtils.ThunkResult<Promise<void>>;
        removeFewItems: (
            ids: any[],
        ) => ReduxTypeUtils.ThunkResult<Promise<void>>;
    } = {
        addConsumer,
        removeConsumer,
        // @ts-ignore
        initialLoadList,
        loadList: loadListSavingState,
        loadListSavingState,
        loadItem,
        updateItem,
        createItem,
        createFewItems,
        removeItem,
        removeFewItems,
    };

    const Actions = {
        ...TypedActions,
        editItem,
    };

    return {
        module,
        Selectors,
        Actions,
    };
};

const _applyKeyToApiMethods = <T extends (...a: any[]) => {}>(
    apiMethods: {
        [key: string]: T;
    },
    key: any,
) => R.mapObjIndexed((method) => R.partial(method, [key]), apiMethods);

const createCommonModule = <D>({
    initialData = undefined,
    entityName,
    entityNamePublic,
    apiMethods,
    key,
}: CommonModuleParams<D>) => {
    const dataFieldKey = 'data';

    type InitialState = {
        data: D;
        isLoaded: boolean;
        isLoading: boolean;
        hasError: boolean;
    };

    const initialState: InitialState = {
        [dataFieldKey]: initialData,
        isLoaded: false,
        isLoading: false,
        hasError: false,
    } as any;

    const module =
        key == null
            ? stateHelper.createModuleState<InitialState>(
                  entityName,
                  initialState,
              )
            : stateHelper.getMultiModule<InitialState>({
                  name: entityName,
                  key,
                  initialState,
              });

    const initialLoadData = reduxHelper.createLoadListAction({
        module,
        apiMethod: apiMethods.loadData,
        entityName,
        fieldKey: dataFieldKey,
    });

    const updateData = reduxHelper.createUpdateListAction({
        module,
        apiMethod: apiMethods.loadData,
        entityName,
        entityNameLocal: entityNamePublic,
        fieldKey: dataFieldKey,
    });

    const loadData = reduxHelper.createLoadListSavingStateAction({
        loadList: initialLoadData,
        updateList: updateData,
        module,
    });

    const {
        getIsLoaded,
        getIsLoading,
        getHasError,
    } = reduxHelper.makeStandardSelectors(module, {
        listKey: dataFieldKey,
    });

    const getData = (state: GlobalState) =>
        module.getPart(state, dataFieldKey) as D;

    const setData = (data: D) => module.setPart(dataFieldKey, data);
    const mergeData = (data: Partial<D>) => async (dispatch, getState) => {
        const existData = getData(getState());
        dispatch(
            setData({
                ...existData,
                ...data,
            }),
        );
    };

    const { addConsumer, removeConsumer } = new ModuleListReloader({
        loadList: updateData,
        initialLoadList: initialLoadData,
        selectIsLoaded: getIsLoaded,
    });

    type Selector<T> = (state: GlobalState) => T;

    const Selectors: {
        getData: ReduxTypeUtils.Selector<D>;
        getIsLoaded: ReduxTypeUtils.Selector<boolean>;
        getIsLoading: ReduxTypeUtils.Selector<boolean>;
        getHasError: ReduxTypeUtils.Selector<boolean>;
    } = {
        getData,
        getIsLoaded,
        getIsLoading,
        getHasError,
    };

    const typedActions: {
        addConsumer: () => ReduxTypeUtils.ThunkResult<Promise<void>>;
        removeConsumer: () => ReduxTypeUtils.ThunkResult<Promise<void>>;
        loadData: () => ReduxTypeUtils.ThunkResult<Promise<D>>;
        initialLoadData: () => ReduxTypeUtils.ThunkResult<Promise<D>>;
    } = {
        addConsumer,
        removeConsumer,
        // @ts-ignore
        initialLoadData,
        loadData,
    };

    const Actions = {
        ...typedActions,
        setData,
        mergeData,
    };

    return {
        module,
        Selectors,
        Actions,
    };
};

export const CommonModule = {
    createCommonListModule,
    createCommonModule,
};
